/**
 * useMobile Hook
 * 
 * CRITICAL: Detects if the current DEVICE is mobile (not just viewport width).
 * 
 * Mobile mode is LOCKED once determined and does NOT change on:
 * - Orientation change
 * - Viewport resize
 * - Browser chrome changes
 * 
 * Detection is based on:
 * 1. User Agent (primary - permanent)
 * 2. Touch capability (secondary)
 * 3. Screen size at initial load (fallback)
 * 
 * RULE: Rotation must NEVER change permissions, features, or visible functionality.
 */

import { useState, useEffect, useRef } from 'react';

/**
 * Detect if device is mobile based on User Agent
 */
function detectMobileByUserAgent() {
  if (typeof navigator === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
  
  // Common mobile device patterns
  const mobilePatterns = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /IEMobile/i,
    /Opera Mini/i,
    /Windows Phone/i,
    /Mobile/i,
    /Tablet/i
  ];
  
  return mobilePatterns.some(pattern => pattern.test(userAgent));
}

/**
 * Detect if device has touch capability
 */
function detectTouchCapability() {
  if (typeof window === 'undefined') return false;
  
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0
  );
}

/**
 * Check initial screen size (only used as fallback)
 * Uses screen.width which is device width, not viewport
 */
function detectByScreenSize() {
  if (typeof window === 'undefined') return false;
  
  // Use screen.width (device width) instead of innerWidth (viewport)
  // Screen width doesn't change with rotation
  const screenWidth = Math.min(window.screen.width, window.screen.height);
  return screenWidth < 768;
}

/**
 * Determine if device is mobile (called ONCE at initialization)
 */
function determineIsMobileDevice() {
  // Priority 1: User Agent (most reliable)
  const byUserAgent = detectMobileByUserAgent();
  if (byUserAgent) return true;
  
  // Priority 2: Touch + small screen combination
  const hasTouch = detectTouchCapability();
  const smallScreen = detectByScreenSize();
  
  if (hasTouch && smallScreen) return true;
  
  // Priority 3: Very small screen (definitely mobile)
  const verySmallScreen = Math.min(window.screen.width, window.screen.height) < 500;
  if (verySmallScreen) return true;
  
  return false;
}

/**
 * useMobile Hook
 * 
 * Returns a LOCKED mobile state that does NOT change on rotation.
 */
export function useMobile() {
  // Use ref to ensure detection happens only ONCE
  const initializedRef = useRef(false);
  const lockedMobileStateRef = useRef(false);
  
  // Initialize mobile state ONCE
  const [isMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    
    // Determine mobile status once and lock it
    if (!initializedRef.current) {
      lockedMobileStateRef.current = determineIsMobileDevice();
      initializedRef.current = true;
      
    }
    
    return lockedMobileStateRef.current;
  });
  
  // NO resize listener - mobile state is LOCKED
  // Rotation/resize does NOT affect mobile detection
  
  return isMobile;
}

/**
 * Hook to get viewport dimensions (for layout only, not mode switching)
 * This CAN change on rotation for layout purposes
 */
export function useViewportSize() {
  const [size, setSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
    isLandscape: typeof window !== 'undefined' ? window.innerWidth > window.innerHeight : false
  }));
  
  useEffect(() => {
    const handleResize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
        isLandscape: window.innerWidth > window.innerHeight
      });
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);
  
  return size;
}

export default useMobile;
